import { EVENT_TYPE } from '@App/app/entities/shared/event-types.enum';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Chart } from 'chart.js';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ArcRotateCameraAnimationConfig } from '@App/app/configs/babylon.config';
import { BroadcastService } from '@App/app/shared/broadcast.service';
import { ThemeService } from '@App/app/shared/services/theme/theme.service';
import { CharElement } from './camera-snap.models';

@UntilDestroy()
@Component({
  selector: 'app-camera-snap',
  templateUrl: './camera-snap.component.html',
  styleUrls: ['./camera-snap.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CameraSnapComponent implements OnInit, OnDestroy {
  chart: Chart;
  isHovered = false;

  dataDonutMenuE = 'DataDonutMenuE';
  dataDonutMenuN = 'DataDonutMenuN';
  dataDonutMenuS = 'DataDonutMenuS';
  dataDonutMenuW = 'DataDonutMenuW';

  constructor(private broadcastService: BroadcastService, private themeService: ThemeService) {}

  // Chart.js doesn't read CSS variables directly. Sample the resolved values
  // from <body> (where Nebular emits the per-theme CSS-custom-property block)
  // so the donut segments follow whichever theme is active.
  private themeColor(name: string): string {
    try {
      return getComputedStyle(document.body).getPropertyValue(`--${name}`).trim();
    } catch {
      // jsdom does not fully implement getComputedStyle().getPropertyValue.
      return '';
    }
  }

  ngOnInit() {
    this.buildChart();
    // Theme can flip while the viewer is open; Chart.js needs new color strings
    // (not CSS variables) so update the dataset in place and call update().
    this.themeService.theme$.pipe(untilDestroyed(this)).subscribe(() => this.applyThemeColors());
  }

  private buildChart() {
    const segmentColor = this.themeColor('background-basic-color-3');
    const hoverColor = this.themeColor('background-basic-color-2');
    const borderColor = this.themeColor('border-basic-color-4');
    this.chart = new Chart('canvas', {
      type: 'doughnut',
      data: {
        labels: [
          this.dataDonutMenuE,
          this.dataDonutMenuS,
          this.dataDonutMenuW,
          this.dataDonutMenuN,
        ],
        datasets: [
          {
            data: [1, 1, 1, 1],
            backgroundColor: new Array(4).fill(segmentColor),
            fill: false,
            hoverBackgroundColor: hoverColor,
            hoverBorderColor: borderColor,
            borderColor,
            borderWidth: 2.0,
          },
        ],
      },
      options: {
        legend: {
          display: false,
        },
        tooltips: {
          enabled: false,
        },
        rotation: 150,
        animation: undefined,

        onClick: (_: unknown, e: any[]) => {
          if (e.length) {
            this.broadcastService.broadcast(EVENT_TYPE.CAMERA_SNAP, e[0]._view.label);
          }
        },
        //TODO: bind this or replace this function with arrow function and use ngClass
        onHover(e: Event) {
          const chartElement: CharElement[] = this.getElementAtEvent(e);
          if (chartElement.length) {
            const worldDirections = Array.from(document.querySelectorAll(`[id^="DataDonutMenu"]`));
            const labelName = chartElement[0]._view?.label;
            const worldDirection = document.querySelector(`[id*="${labelName}"]`);

            // Change color of hovered segment
            if (worldDirection) {
              worldDirection.className = `camera_snap__direction-text--hovered-${labelName}`;
            }

            // Change color of not hovered segments
            worldDirections.forEach((el) => {
              if (el.id !== labelName) {
                el.className = `camera_snap__direction-text`;
              }
            });
          } else {
            const worldDirections = document.querySelectorAll(`[id^="DataDonutMenu"]`);
            worldDirections.forEach((el) => {
              el.className = `camera_snap__direction-text`;
            });
          }
        },
        cutoutPercentage: 77,
      },
    });
  }

  private applyThemeColors() {
    const dataset = this.chart?.data?.datasets?.[0];
    if (!dataset) return;
    const segmentColor = this.themeColor('background-basic-color-3');
    const hoverColor = this.themeColor('background-basic-color-2');
    const borderColor = this.themeColor('border-basic-color-4');
    dataset.backgroundColor = new Array(4).fill(segmentColor);
    dataset.hoverBackgroundColor = hoverColor;
    dataset.hoverBorderColor = borderColor;
    dataset.borderColor = borderColor;
    this.chart.update();
  }

  ngOnDestroy() {
    this.chart.destroy();
  }

  onNadirButton() {
    this.broadcastService.broadcast(
      EVENT_TYPE.CAMERA_SNAP,
      ArcRotateCameraAnimationConfig.TOP.name,
    );
  }
}
